import {
  parsePreviewShellConfig,
  parseShellConfig,
  resolveFormFactor,
} from '@/lib/site-agent/placements/parse-shell-config'

import type {ShellConfig} from './types'

export {DEFAULT_FONT_FAMILY} from './fonts'
export {parsePreviewShellConfig, parseShellConfig, resolveFormFactor}

export type SdkConfigResource = 'experiences' | 'placements'
export type SdkConfigRequestResource = SdkConfigResource | 'auto'

export type ResolvedShellConfig = {
  shellConfig: ShellConfig
  resource: SdkConfigResource
  embedId: string
}

function isResolvedConfigResponse(data: unknown): data is {
  runtimeResource: SdkConfigResource
  runtimeId: string
  config: Record<string, unknown>
} {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false
  const record = data as Record<string, unknown>
  return (
    (record.runtimeResource === 'experiences' || record.runtimeResource === 'placements') &&
    typeof record.runtimeId === 'string' &&
    Boolean(record.config) &&
    typeof record.config === 'object' &&
    !Array.isArray(record.config)
  )
}

function getConfigPath(baseUrl: string, embedId: string, resource: SdkConfigRequestResource): string {
  if (resource === 'placements') {
    return `${baseUrl}/api/sdk/config/placements/${embedId}`
  }
  if (resource === 'auto') {
    return `${baseUrl}/api/sdk/config/resolve/${embedId}`
  }
  return `${baseUrl}/api/sdk/config/${embedId}`
}

function getDefaultRuntimeResource(resource: SdkConfigRequestResource): SdkConfigResource {
  return resource === 'placements' ? 'placements' : 'experiences'
}

export async function fetchShellConfig(
  baseUrl: string,
  embedId: string,
  options?: {
    parentUrl?: string
    formFactorOverride?: string
    fromPreview?: boolean
    resource?: SdkConfigResource
  }
): Promise<ShellConfig | null> {
  const resolved = await fetchResolvedShellConfig(baseUrl, embedId, options)
  return resolved?.shellConfig ?? null
}

export async function fetchResolvedShellConfig(
  baseUrl: string,
  embedId: string,
  options?: {
    parentUrl?: string
    formFactorOverride?: string
    fromPreview?: boolean
    resource?: SdkConfigRequestResource
  }
): Promise<ResolvedShellConfig | null> {
  try {
    const params = new URLSearchParams()
    if (options?.parentUrl) params.set('parentUrl', options.parentUrl)
    if (options?.formFactorOverride) params.set('formFactor', options.formFactorOverride)
    if (options?.fromPreview) params.set('fromPreview', 'true')
    const qs = params.toString()
    const resource = options?.resource ?? 'experiences'
    const configPath = getConfigPath(baseUrl, embedId, resource)
    const url = `${configPath}${qs ? `?${qs}` : ''}`
    const response = await fetch(url, {
      method: 'GET',
      headers: {Accept: 'application/json'},
    })

    if (!response.ok) {
      console.error(`[Anagram] Config fetch failed: ${response.status}`)
      return null
    }

    const data = (await response.json()) as unknown
    if (isResolvedConfigResponse(data)) {
      return {
        shellConfig: parseShellConfig(data.config),
        resource: data.runtimeResource,
        embedId: data.runtimeId,
      }
    }

    return {
      shellConfig: parseShellConfig(data as Record<string, unknown>),
      resource: getDefaultRuntimeResource(resource),
      embedId,
    }
  } catch (error) {
    console.error('[Anagram] Config fetch error:', error)
    return null
  }
}
